import { memo, useState } from "react";
import { Timeline, Loader } from "@mantine/core";
import {
	Ellipsis,
	Check,
	ChevronUp,
	ChevronDown,
	X as Close,
	Settings,
	RotateCcw,
} from "lucide-react";
import { useAccessRequest } from "@/context/access-request-context";
import UserTaskData from "@/components/workflow/UserTaskData";
import ServiceTaskData from "./ServiceTaskData";
import IncidentData from "./IncidentData";
import { removeDelimeterFromString } from "@/lib/utils";

const ApplicationTracker = () => {
	const [expandedSections, setExpandedSections] = useState({});
	const {
		history: taskHistory,
		incidents,
		isLoadingHistory,
		retryAction,
		retryingRequest,
	} = useAccessRequest();

	const toggleSection = (index) => {
		setExpandedSections((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const ItemTitle = ({
		taskHistory,
		index,
	}: { taskHistory: Task; index: number }) => (
		<div className="flex gap-3 items-center  justify-between">
			<div className="flex gap-3 items-center">
				<span className="font-regular flex items-center gap-3">
					{taskHistory?.name}
				</span>
			</div>
			<div>
				<button
					className="text-gray-400 hover:text-gray-300 transition-colors"
					type="button"
					onClick={() => toggleSection(index)}
				>
					{expandedSections[index] ? <ChevronUp /> : <ChevronDown />}
				</button>
			</div>
		</div>
	);

	const IncidentTitle = ({
		incident,
		key,
	}: { incident: Incident; key: string }) => (
		<div className="flex gap-3 items-center  justify-between">
			<div className="flex gap-3 items-center">
				<span className="font-regular flex items-center gap-3">
					{removeDelimeterFromString(incident?.activityId)}
					<button
						disabled={retryingRequest}
						type="button"
						onClick={() => retryAction(incident)}
						className=" flex text-sm gap-3 rounded-md bg-transparent border hover:bg-primaryGreen-500 px-2 py-1 items-center"
					>
						Retry{" "}
						<span className={retryingRequest ? "animate-spin" : ""}>
							<RotateCcw size={15} />
						</span>
					</button>
				</span>
			</div>
			<div>
				<button
					className="text-gray-400 hover:text-gray-300 transition-colors"
					type="button"
					onClick={() => toggleSection(key)}
				>
					{expandedSections[key] ? <ChevronUp /> : <ChevronDown />}
				</button>
			</div>
		</div>
	);

	const displayTaskIcon = ({
		action,
		status,
	}: { action: string; status: string; type?: string }) => {
		return status === "completed" ? (
			action === "approve" ? (
				<Check size={10} />
			) : (
				<Close size={10} />
			)
		) : (
			<Ellipsis size={10} />
		);
	};

	return (
		<div className="space-y-6">
			<Timeline
				active={taskHistory.length}
				bulletSize={20}
				lineWidth={2}
				align="left"
			>
				{!isLoadingHistory ? (
					taskHistory?.map((task, index) =>
						task.activityType === "userTask" ? (
							<Timeline.Item
								className="text-textColor"
								key={task?.id}
								title={<ItemTitle taskHistory={task} index={index} />}
								bullet={displayTaskIcon({
									action: task?.variables?.response,
									status: task?.status,
									type: task?.activityType,
								})}
								color={
									task.status === "completed"
										? task?.variables?.response === "approve"
											? "green"
											: "red"
										: "gray"
								}
							>
								<UserTaskData
									expandedSections={expandedSections}
									toggleArrow={toggleSection}
									taskHistory={task}
									index={index}
									key={task?.id}
								/>
							</Timeline.Item>
						) : (
							<Timeline.Item
								className="text-textColor"
								key={task?.id}
								title={<ItemTitle taskHistory={task} index={index} />}
								bullet={<Settings size={10} />}
								color={
									task?.variables.workflow_current_step_status === "completed"
										? "green"
										: task?.variables.workflow_current_step_status === "failed"
											? "red"
											: "gray"
								}
							>
								<ServiceTaskData
									expandedSections={expandedSections}
									toggleArrow={toggleSection}
									taskHistory={task}
									index={index}
									key={task?.id}
								/>
							</Timeline.Item>
						),
					)
				) : (
					<Loader color="green" />
				)}
				{incidents.length > 0 &&
					incidents.map((incident, index) => (
						<Timeline.Item
							className="text-textColor"
							key={incident?.id}
							title={<IncidentTitle incident={incident} key={incident.id} />}
							bullet={<Settings size={10} />}
							color="red"
						>
							<IncidentData
								expandedSections={expandedSections}
								toggleArrow={toggleSection}
								incident={incident}
								index={index}
								key={incident?.id}
							/>
						</Timeline.Item>
					))}
			</Timeline>
		</div>
	);
};

export default memo(ApplicationTracker);
